import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { CuratedPromptCard } from "@/components/curated-prompt-card";
import { getAllPrompts } from "@/lib/prompts";

interface CategorySearch {
  page?: number;
}

const CATEGORY_INFO = {
  product: {
    title: "Product Photography AI Prompts",
    description:
      "Professional product photography AI prompts for ecommerce, catalogs, and marketing materials. Create stunning product images with detailed lighting and composition instructions.",
  },
  food: {
    title: "Food Photography AI Prompts",
    description:
      "Gourmet food photography prompts for restaurants, menus, and culinary content. Generate mouthwatering images with perfect styling and dramatic lighting.",
  },
  lifestyle: {
    title: "Lifestyle Photography AI Prompts",
    description:
      "Lifestyle and everyday scene AI prompts for authentic, relatable imagery. Perfect for social media, blogs, and lifestyle brands.",
  },
  design: {
    title: "Design & Creative AI Prompts",
    description:
      "Creative design prompts for digital art, illustrations, and visual content. Ideal for designers, marketers, and content creators.",
  },
};

export const Route = createFileRoute("/prompts/category/$category")({
  validateSearch: (search: Record<string, unknown>): CategorySearch => {
    return {
      page: Number(search?.page) || 1,
    };
  },

  // SSR loader for category prompts
  loader: async ({ params }) => {
    try {
      const allPrompts = await getAllPrompts({ data: undefined });
      const categoryPrompts = allPrompts.filter(
        (p) => p.category === params.category
      );

      return {
        prompts: categoryPrompts,
        category: params.category,
      };
    } catch (error) {
      console.error("[category loader] Error:", error);
      return {
        prompts: [],
        category: params.category,
      };
    }
  },

  // SEO meta tags for category pages
  head: ({ loaderData }) => {
    const { category, prompts } = loaderData;
    const categoryInfo =
      CATEGORY_INFO[category as keyof typeof CATEGORY_INFO] ||
      CATEGORY_INFO.product;

    return {
      meta: [
        {
          title: `${categoryInfo.title} - Free Examples | Images AI`,
        },
        {
          name: "description",
          content: categoryInfo.description,
        },
        {
          name: "keywords",
          content: `${category} AI prompts, ${category} photography AI, AI image generation ${category}, FLUX 3 ${category} prompts, GPT Image 2.5 ${category}, Ideogram V4 ${category}, text to image ${category}`,
        },
        {
          property: "og:title",
          content: `${categoryInfo.title} | Images AI`,
        },
        {
          property: "og:description",
          content: categoryInfo.description,
        },
        {
          property: "og:type",
          content: "website",
        },
        {
          property: "og:url",
          content: `https://images-ai.app/prompts/category/${category}`,
        },
        {
          property: "twitter:card",
          content: "summary_large_image",
        },
        {
          property: "twitter:title",
          content: categoryInfo.title,
        },
        {
          property: "twitter:description",
          content: categoryInfo.description,
        },
      ],
      links: [
        {
          rel: "canonical",
          href: `https://images-ai.app/prompts/category/${category}`,
        },
      ],
      // JSON-LD structured data
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: categoryInfo.title,
            description: categoryInfo.description,
            url: `https://images-ai.app/prompts/category/${category}`,
            about: category,
            publisher: {
              "@type": "Organization",
              name: "Images AI",
              url: "https://images-ai.app",
            },
            numberOfItems: prompts.length,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@id": "https://images-ai.app/",
                  name: "Home",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@id": "https://images-ai.app/prompts",
                  name: "Prompts",
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@id": `https://images-ai.app/prompts/category/${category}`,
                  name: categoryInfo.title,
                },
              },
            ],
          }),
        },
      ],
    };
  },

  component: CategoryPage,
});

function CategoryPage() {
  const { prompts, category } = Route.useLoaderData();
  const { session } = Route.useRouteContext();
  const categoryInfo =
    CATEGORY_INFO[category as keyof typeof CATEGORY_INFO] || CATEGORY_INFO.product;

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb Navigation */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link
              to="/prompts"
              className="hover:text-foreground transition-colors"
            >
              Prompts
            </Link>
            <span>/</span>
            <span className="text-foreground font-medium capitalize">
              {category}
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold">
              {categoryInfo.title}
            </h1>
            <p className="text-lg text-muted-foreground">
              {categoryInfo.description}
            </p>
            <div className="pt-4">
              {session ? (
                <Link to="/dashboard" preload="intent">
                  <Button size="lg" className="rounded-full px-8">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/signup" preload="intent">
                  <Button size="lg" className="rounded-full px-8">
                    Try These Prompts Free
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Prompts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {prompts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No prompts available in this category yet. Check back soon!
              </p>
              <Link to="/prompts" className="inline-block mt-4">
                <Button variant="outline" className="rounded-full">
                  Browse All Prompts
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-sm text-muted-foreground">
                  {prompts.length} {prompts.length === 1 ? "prompt" : "prompts"}{" "}
                  found
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {prompts.map((prompt) => (
                  <CuratedPromptCard key={prompt.slug} prompt={prompt} />
                ))}
              </div>
            </>
          )}

          {/* CTA Section */}
          <div className="mt-20 text-center space-y-6">
            <h2 className="text-3xl font-bold">Ready to create your own?</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Generate {category} visuals with FLUX 3, GPT Image 2.5, Qwen Image
              3, and Seedream 5. Free to start — no credit card required.
            </p>
            {session ? (
              <Link to="/dashboard">
                <Button size="lg" className="rounded-full px-12">
                  Go to Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-12">
                  Get Started Free
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
